import React from 'react';
import {Chip, Tooltip} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {CheckRounded, EditRounded, WarningRounded} from "@mui-symbols-material/w300";
import {EventType} from "../../codegen/graphql";
import {isContaminationDeclarationsEnabled} from "../../utils/contaminationDeclarationsEnabled";
import {useDeclarationForEvent} from "../../hooks/useDeclarationForEvent";
import {useOpenContaminationDeclaration} from "../../hooks/useOpenContaminationDeclaration";

interface Props {
    eventId: string;
    eventType?: string | null;
}

/**
 * Missing-/present-DoC indicator for an ASSET_REMOVE event (event history,
 * asset declarations list). Renders nothing for other event types, while
 * loading, when the lookup fails (no node access), or — with no declaration
 * started — where the form is not offered at all: a nudge, never noise.
 * Clicking opens the declaration, creating it first when allowed.
 */
const ContaminationDeclarationStatusChip = ({eventId, eventType}: Props) => {
    const {t} = useTranslation();
    const enabled = isContaminationDeclarationsEnabled() && eventType === EventType.ASSET_REMOVE;
    const {declaration, readiness, available, loading, error} = useDeclarationForEvent(eventId, !enabled);
    const {openForEvent, busy, canCreate} = useOpenContaminationDeclaration();

    if (!enabled || loading || error) return null;

    // Nothing started and the form is not offered for this asset's owner: stay away entirely.
    // The missing-chip is a nudge to start one, and a permanent warning about a gap that can
    // never be filled is noise — a removal in a region the form does not cover is complete
    // without a declaration. Keyed on availability alone, *not* on `canCreate`: a user who may
    // not start one still wants to see that the removal is missing its paperwork.
    if (!declaration && !available) return null;

    // The resource key says "may work with declarations", availability says "is the form offered
    // for this asset's owner at all" — both have to hold. Below, in the no-declaration branch,
    // availability is already guaranteed by the guard above; `canStart` keeps the rule in one
    // place rather than relying on the reader to trace that.
    const canStart = canCreate && available;

    const open = (e: React.MouseEvent) => {
        e.stopPropagation();
        openForEvent(eventId);
    };

    if (!declaration) {
        const chip = (
            <Chip
                variant="soft"
                color="warning"
                size="sm"
                startDecorator={<WarningRounded fontSize="small"/>}
                onClick={canStart ? open : undefined}
                disabled={busy}
                data-testid="doc-status-chip"
                data-status="missing"
            >
                {t('No DoC')}
            </Chip>
        );
        return (
            <Tooltip
                variant="soft"
                placement="top"
                title={canStart
                    ? t('No declaration of contamination attached – click to create one')
                    : t('No declaration of contamination attached')}
            >
                {chip}
            </Tooltip>
        );
    }

    const ready = !!readiness?.ready;
    return (
        <Tooltip variant="soft" placement="top" title={t('Open declaration')}>
            <Chip
                variant="soft"
                color={ready ? 'success' : 'warning'}
                size="sm"
                startDecorator={ready ? <CheckRounded fontSize="small"/> : <EditRounded fontSize="small"/>}
                onClick={open}
                disabled={busy}
                data-testid="doc-status-chip"
                data-status={ready ? 'complete' : 'incomplete'}
            >
                {ready ? t('DoC complete') : t('DoC incomplete')}
            </Chip>
        </Tooltip>
    );
};

export default ContaminationDeclarationStatusChip;
